import { Alert, Box, Button } from '@mui/material';
import React from 'react';
import { CONSTANTS, PEDIGREE } from 'src/components/Constants';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import TextInput from 'src/components/UI/input/TextInput';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  ChangedField,
  UnsavedChangeCount,
  useChangedFields,
  useUnsavedFormGuard
} from 'src/components/UI/input/changed-field';
import UnsavedChangesDialog from 'src/components/UI/dialog/UnsavedChangesDialog';

function TextModal({
  node,
  memberRecords,
  setMemberRecords,
  setPedigreeState,
  handleClose,
  menu,
  editText,
  // Filled with this modal's guarded close, for the host's backdrop and Escape
  // (TGCLOUD-1902).
  guardRef
}) {
  const t = useAutoTranslation();

  const [comment, setComment] = React.useState(
    (editText ? node?.editText : node?.personNote) ?? ''
  );

  const inputRef = React.useRef(null);

  // UNSAVED CHANGES (TGCLOUD-1902): the text against the person's as opened.
  // The symbol's text is compared as it would be stored (4 characters, capitals).
  const asStored = editText
    ? (text) =>
        String(text ?? '')
          .slice(0, 4)
          .toUpperCase()
    : (text) => text;
  const [initialText] = React.useState(comment);
  const changes = useChangedFields({
    baseline: { text: initialText },
    values: { text: comment },
    fields: [{ name: 'text', read: (v) => asStored(v.text) }]
  });
  useUnsavedFormGuard(changes.unsavedCount);
  const [unsavedPrompt, setUnsavedPrompt] = React.useState(false);
  const requestClose = () => {
    if (changes.unsavedCount > 0) setUnsavedPrompt(true);
    else handleClose();
  };
  React.useEffect(() => {
    if (!guardRef) return undefined;
    guardRef.current = requestClose;
    return () => {
      guardRef.current = null;
    };
  });

  React.useEffect(() => {
    if (inputRef.current) {
      const length = inputRef.current.value.length;
      inputRef.current.focus();

      inputRef.current.setSelectionRange(length, length);
    }
  }, [node]);

  const onSave = () => {
    const data = node;

    const idx = memberRecords.findIndex((person) => data.pid === person.pid);

    editText
      ? (memberRecords[idx] = {
          ...memberRecords[idx],
          editText: String(comment).slice(0, 4).toUpperCase()
        })
      : (memberRecords[idx] = {
          ...memberRecords[idx],
          personNote: comment
        });

    setMemberRecords([...memberRecords]);

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    handleClose();
  };

  const onEditChange = (e) => {
    setComment(e.target.value);
  };

  const handleClear = () => {
    setComment('');
  };

  return (
    <div>
      <UnsavedChangesDialog
        open={unsavedPrompt}
        count={changes.unsavedCount}
        onCancel={() => setUnsavedPrompt(false)}
        onDiscard={() => {
          setUnsavedPrompt(false);
          handleClose();
        }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {editText && (
          <Alert severity="info">
            {t(
              'Only the first 4 characters will be displayed inside the icon.'
            )}
          </Alert>
        )}
        <div className="user-form">
          <FormLabel component="label" className="form-label">
            {editText ? 'Edit Text' : 'Person Note'}
          </FormLabel>
        </div>
        <ChangedField name="text" changed={changes.changed.text}>
          <TextInput
            type={editText ? 'text' : 'textarea'}
            rows={8}
            value={comment}
            onChange={(e) =>
              editText ? onEditChange(e) : setComment(e.target.value)
            }
            placeholder={''}
            ref={inputRef}
            autoFocus
          />
        </ChangedField>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'end'
          }}
        >
          {menu && (
            <Button size="small" variant="outlined" onClick={handleClear}>
              {t('clear')}
            </Button>
          )}

          <UnsavedChangeCount count={changes.count} sx={{ mr: 'auto' }} />
          {menu && (
            <Button size="small" variant="outlined" onClick={requestClose}>
              {t('cancel')}
            </Button>
          )}
          <Button size="small" variant="contained" onClick={onSave}>
            {t('save')}
          </Button>
        </Box>
      </Box>
    </div>
  );
}

export default TextModal;
